import React, { useState } from 'react';
import { ArrowRight, Package } from 'lucide-react';
import BlurFade from '../primitives/BlurFade';
import { Section } from '../layout/Section';
import { Container } from '../layout/Container';
import { Eyebrow, Headline, Body } from '../layout/typography';
import { submitLead } from '../../lib/api';
import { useLang } from '../../LangContext';
import { mediaAlt } from '../../lib/mediaSeo';

/**
 * SampleKitBlock — "free stalen / Gratis Stoffmuster" lead magnet.
 *
 * Clip-shade.de style: trigger a low-commitment opt-in by mailing a free
 * gauze sample pack. The user shares only name + email + address; this is
 * a softer conversion path than the main offer form.
 *
 * Submissions go to the same /api/leads endpoint with source=sample-kit
 * and product=stalen so the backend can route them differently if needed.
 *
 * Props: { id, testId, tone, rhythm, eyebrow, title, body, fields, items,
 *          submitLabel, success, error, lang }
 */
export default function SampleKitBlock({
  id = 'gratis-stalen',
  testId = 'sample-kit',
  tone = 'sand',
  rhythm = 'relaxed',
  eyebrow,
  title,
  body,
  items = [],
  fields = {},
  submitLabel = 'Stuur mij gratis stalen',
  success,
  error,
  lang = 'nl',
  imageSrc,
}) {
  const { overlay } = useLang();
  const [form, setForm] = useState({ name: '', email: '', address: '' });
  const [status, setStatus] = useState('idle');

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await submitLead({
        name: form.name,
        email: form.email,
        phone: null,
        message: `Adres voor stalenpakket: ${form.address}`,
        product: 'stalen',
        language: lang,
        source: 'sample-kit',
      });
      setStatus('success');
      setForm({ name: '', email: '', address: '' });
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  return (
    <Section id={id} data-testid={testId} tone={tone} rhythm={rhythm}>
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-14 items-start">
          {/* Left — copy + form */}
          <div className="lg:col-span-7">
            {eyebrow && <BlurFade><Eyebrow className="mb-6">{eyebrow}</Eyebrow></BlurFade>}
            {title && (
              <BlurFade delay={0.05}>
                <Headline size="lg">{title}</Headline>
              </BlurFade>
            )}
            {body && (
              <BlurFade delay={0.12}>
                <Body className="mt-6 text-[15.5px] lg:text-[17px] leading-relaxed max-w-xl">{body}</Body>
              </BlurFade>
            )}

            {items.length > 0 && (
              <BlurFade delay={0.18}>
                <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[#1F1D1A] text-[14px]">
                  {items.map((it) => (
                    <li key={it} className="inline-flex items-start gap-2">
                      <Package className="w-3.5 h-3.5 mt-1 text-[#F26B1A] shrink-0" strokeWidth={2.4} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </BlurFade>
            )}

            <BlurFade delay={0.24}>
              <form onSubmit={onSubmit} data-testid="sample-kit-form"
                className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                <input
                  required
                  placeholder={fields.name || 'Naam'}
                  value={form.name}
                  onChange={onChange('name')}
                  data-testid="sample-kit-name"
                  autoComplete="name"
                  className="glass-field sm:col-span-1"
                />
                <input
                  required
                  type="email"
                  placeholder={fields.email || 'E-mail'}
                  value={form.email}
                  onChange={onChange('email')}
                  data-testid="sample-kit-email"
                  autoComplete="email"
                  className="glass-field sm:col-span-1"
                />
                <input
                  required
                  placeholder={fields.address || 'Adres (straat, nr, postcode, plaats)'}
                  value={form.address}
                  onChange={onChange('address')}
                  data-testid="sample-kit-address"
                  autoComplete="street-address"
                  className="glass-field sm:col-span-2"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid="sample-kit-submit"
                  data-lead-source="sample-kit"
                  className="pill pill-leaf sm:col-span-2 justify-center"
                >
                  {status === 'loading' ? '…' : submitLabel}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
              {status === 'success' && success && (
                <p data-testid="sample-kit-success" className="mt-4 text-[#F26B1A] text-[14px] font-medium">{success}</p>
              )}
              {status === 'error' && error && (
                <p data-testid="sample-kit-error" className="mt-4 text-red-600 text-[14px]">{error}</p>
              )}
            </BlurFade>
          </div>

          {/* Right — visual: stylised package */}
          <div className="lg:col-span-5">
            <BlurFade delay={0.18} offset={20}>
              <div className="relative rounded-[24px] overflow-hidden aspect-[4/5] depth-card bg-white">
                {imageSrc ? (
                  <img src={imageSrc} alt={mediaAlt(overlay, imageSrc, lang, title || eyebrow || '')} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FAF4E6] to-[#F2DFB3] flex items-center justify-center">
                    <Package className="w-20 h-20 text-[#F26B1A]/40" strokeWidth={1.4} />
                  </div>
                )}
                <span className="absolute top-5 left-5 inline-flex items-center px-3 py-1 rounded-full bg-[#F26B1A] text-white text-[10.5px] uppercase tracking-[0.15em] font-semibold">
                  Gratis · €0
                </span>
              </div>
            </BlurFade>
          </div>
        </div>
      </Container>
    </Section>
  );
}
